import {
  Box,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { KeyboardArrowDown, Menu as MenuIcon } from "@mui/icons-material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { StudyLanguage } from "./landingLanguage";

const EMBRACE_LOGO_SRC = "/embrace-logo-expanded.png";

type LandingPageHeaderProps = {
  languages?: StudyLanguage[];
  lang?: string;
  onLanguageChange?: (lang: string) => void;
};

const LanguageSelect = ({
  languages,
  lang,
  onLanguageChange,
  fullWidth = false,
  selectAriaLabel,
}: {
  languages: StudyLanguage[];
  lang: string;
  onLanguageChange: (lang: string) => void;
  fullWidth?: boolean;
  selectAriaLabel: string;
}) => (
  <Select
    value={lang}
    onChange={(e) => onLanguageChange(e.target.value)}
    size="small"
    fullWidth={fullWidth}
    IconComponent={KeyboardArrowDown}
    aria-label={selectAriaLabel}
    sx={{
      minWidth: fullWidth ? undefined : 140,
      height: fullWidth ? "48px" : "36px",
      fontSize: "14px",
      fontWeight: 500,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#E0E3EB",
      },
    }}
  >
    {languages.map((item) => (
      <MenuItem key={item.languageCode} value={item.languageCode}>
        {item.languageLabel || item.languageCode}
      </MenuItem>
    ))}
  </Select>
);

const LandingPageHeader = ({
  languages = [],
  lang = "en",
  onLanguageChange,
}: LandingPageHeaderProps) => {
  const { t } = useTranslation("landing-page");
  const isMobile = useMediaQuery("(max-width:900px)");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const showLanguageSwitcher =
    languages.length > 1 && lang && onLanguageChange;

  const handleLanguageChange = (selectedLang: string) => {
    onLanguageChange?.(selectedLang);
    setDrawerOpen(false);
  };

  return (
    <>
      <Box
        component="header"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          width: "100%",
          height: "4rem",
          borderBottom: "1px solid #F3E8EC",
          backgroundColor: "hsl(0 0% 100% / 0.8)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          padding: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={EMBRACE_LOGO_SRC}
            alt="EMBRACE Research"
            sx={{
              height: { xs: 36, md: 40 },
              width: "auto",
              objectFit: "contain",
            }}
          />
        </Box>

        {showLanguageSwitcher && !isMobile && (
          <LanguageSelect
            languages={languages}
            lang={lang}
            onLanguageChange={handleLanguageChange}
            selectAriaLabel={t("HEADER.SELECT_LANGUAGE_ARIA")}
          />
        )}

        {showLanguageSwitcher && isMobile && (
          <IconButton
            aria-label={t("HEADER.OPEN_MENU_ARIA")}
            onClick={() => setDrawerOpen(true)}
            sx={{ color: "#111827" }}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      {showLanguageSwitcher && isMobile && (
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { minWidth: 280 } }}
        >
          <Box sx={{ p: 2, pt: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1.5, color: "#6B7280", fontWeight: 600 }}
            >
              {t("HEADER.LANGUAGE_LABEL")}
            </Typography>
            <LanguageSelect
              languages={languages}
              lang={lang}
              onLanguageChange={handleLanguageChange}
              fullWidth
              selectAriaLabel={t("HEADER.SELECT_LANGUAGE_ARIA")}
            />
          </Box>
        </Drawer>
      )}
    </>
  );
};

export default LandingPageHeader;
